import { AppearanceDialog } from '@/components/layout/appearance-dialog'
import { GlobalSearch } from '@/components/layout/global-search'
import { OnboardingChromeProvider, type OnboardingChrome } from '@/components/onboarding/chrome-context'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAppConfig } from '@/hooks/use-app-config'
import { useAppearance, type Appearance } from '@/hooks/use-appearance'
import { getInitials } from '@/lib/format'
import { useRepoMeta } from '@/lib/repo-context'
import { isReservedTopLevelPath } from '#/lib/reserved-paths'
import { cn } from '@/lib/utils'
import {
  BookMarked,
  Building2,
  ChevronDown,
  CircleDot,
  Compass,
  Flag,
  GitPullRequest,
  Home,
  KeyRound,
  LogOut,
  Plus,
  Settings,
  SunMoon,
  UserRound
} from 'lucide-react'
import { Suspense, lazy, useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'

const OnboardingGuide = lazy(() =>
  import('@/components/onboarding/onboarding-guide').then((module) => ({
    default: module.OnboardingGuide,
  })),
)

interface ShellUser {
  email?: string | null
  image?: string | null
  name?: string | null
  username?: string | null
}

interface AppShellProps {
  children: ReactNode
  isPending?: boolean
  onSignOut: () => void
  session?: { user?: ShellUser | null } | null
}

function parseRepoFromPathname(pathname: string): { name: string; owner: string } | null {
  const parts = pathname.split('/').filter(Boolean)
  if (parts.length < 2) return null
  if (isReservedTopLevelPath(parts[0])) return null
  return { owner: parts[0], name: parts[1] }
}

function parseRepoRefFromPathname(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean)
  if ((parts[2] === 'tree' || parts[2] === 'blob') && parts[3]) {
    try {
      return decodeURIComponent(parts[3])
    } catch {
      return parts[3]
    }
  }
  return null
}

const APPEARANCE_LABELS: Record<Appearance, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
}

export function AppShell({ children, isPending = false, onSignOut, session }: AppShellProps) {
  const pathname = useLocation().pathname
  const [appearanceOpen, setAppearanceOpen] = useState(false)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const [accountMenuLocked, setAccountMenuLocked] = useState(false)
  const [createMenuLocked, setCreateMenuLocked] = useState(false)
  const [tourActive, setTourActive] = useState(false)
  const tourActiveRef = useRef(false)
  tourActiveRef.current = tourActive
  const accountMenuLockedRef = useRef(false)
  const createMenuLockedRef = useRef(false)
  const searchOpenerRef = useRef<(() => void) | null>(null)
  const { registrationEnabled } = useAppConfig()
  const repoMeta = useRepoMeta()
  const user = session?.user ?? null

  const registerSearchOpener = useCallback((open: () => void) => {
    searchOpenerRef.current = open
    return () => {
      if (searchOpenerRef.current === open) searchOpenerRef.current = null
    }
  }, [])
  const openAccountMenu = useCallback(() => {
    accountMenuLockedRef.current = true
    setAccountMenuLocked(true)
    setAccountMenuOpen(true)
  }, [])
  const closeAccountMenu = useCallback(() => {
    accountMenuLockedRef.current = false
    setAccountMenuLocked(false)
    setAccountMenuOpen(false)
  }, [])
  const onAccountMenuOpenChange = useCallback((open: boolean) => {
    if (!open && accountMenuLockedRef.current) return
    if (!open) {
      accountMenuLockedRef.current = false
      setAccountMenuLocked(false)
    }
    setAccountMenuOpen(open)
  }, [])
  const openCreateMenu = useCallback(() => {
    createMenuLockedRef.current = true
    setCreateMenuLocked(true)
    setCreateMenuOpen(true)
  }, [])
  const closeCreateMenu = useCallback(() => {
    createMenuLockedRef.current = false
    setCreateMenuLocked(false)
    setCreateMenuOpen(false)
  }, [])
  const onCreateMenuOpenChange = useCallback((open: boolean) => {
    if (!open && createMenuLockedRef.current) return
    if (!open) {
      createMenuLockedRef.current = false
      setCreateMenuLocked(false)
    }
    setCreateMenuOpen(open)
  }, [])
  const openSearch = useCallback(() => searchOpenerRef.current?.(), [])

  const chrome = useMemo<OnboardingChrome>(
    () => ({
      get tourActive() {
        return tourActiveRef.current
      },
      setTourActive,
      openAccountMenu,
      closeAccountMenu,
      openCreateMenu,
      closeCreateMenu,
      registerSearchOpener,
      openSearch,
    }),
    [
      closeAccountMenu,
      closeCreateMenu,
      openAccountMenu,
      openCreateMenu,
      openSearch,
      registerSearchOpener,
    ],
  )
  const repoRoute = repoMeta ?? parseRepoFromPathname(pathname)
  const repoSearchScope = repoRoute
    ? {
      owner: repoRoute.owner,
      repo: repoRoute.name,
      ref: parseRepoRefFromPathname(pathname),
    }
    : null

  const isMarketingHome = pathname === '/' && !user

  if (isMarketingHome) {
    return <div className="min-h-svh">{children}</div>
  }

  return (
    <OnboardingChromeProvider value={chrome}>
      <div className="flex min-h-svh flex-col bg-background">
        <header className="sticky top-0 z-40 bg-background">
          <div className="grid h-12 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-3 sm:px-4">
            <Link
              to="/"
              className="flex items-center justify-self-start outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label="Kilio home"
            >
              <img
                src="/logos/v1/navigation/logo-on-light.svg"
                alt=""
                width={73}
                height={32}
                className="h-8 w-auto dark:hidden"
              />
              <img
                src="/logos/v1/navigation/logo-on-dark.svg"
                alt=""
                width={73}
                height={32}
                className="hidden h-8 w-auto dark:block"
              />
            </Link>

            <div data-tour="global-search">
              <GlobalSearch scope={repoSearchScope} showSyntaxHelp={Boolean(user)} />
            </div>

            <div className="flex items-center gap-1.5 justify-self-end">
              {user ? (
                <>
                  <DropdownMenu
                    open={createMenuOpen}
                    onOpenChange={onCreateMenuOpenChange}
                    modal={!tourActive && !createMenuLocked}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label="Create new"
                        className="px-2 sm:px-2.5"
                        data-tour="create-menu"
                      >
                        <Plus data-icon="inline-start" />
                        <span className="hidden sm:inline">New</span>
                        <ChevronDown className="hidden sm:block" data-icon="inline-end" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className={cn('w-52', createMenuLocked && 'z-[10001]')}
                      onPointerDownOutside={createMenuLocked ? preventMenuDismiss : undefined}
                      onFocusOutside={createMenuLocked ? preventMenuDismiss : undefined}
                      onInteractOutside={createMenuLocked ? preventMenuDismiss : undefined}
                      onEscapeKeyDown={createMenuLocked ? preventMenuDismiss : undefined}
                    >
                      <DropdownMenuGroup>
                        <DropdownMenuItem asChild>
                          <Link to="/new" data-tour="new-repository">New repository</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/new?mode=import">Import repository</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/org/new">New organization</Link>
                        </DropdownMenuItem>
                        {repoRoute ? (
                          <DropdownMenuItem asChild>
                            <Link to={`/${repoRoute.owner}/${repoRoute.name}/pull/new`}>
                              New pull request
                            </Link>
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <UserMenu
                    compact
                    displayName={user.username || user.name || user.email || 'Account'}
                    email={user.email ?? null}
                    image={user.image ?? null}
                    initials={getInitials(user.username || user.name || user.email || 'KL')}
                    username={user.username ?? null}
                    open={accountMenuOpen}
                    onOpenChange={onAccountMenuOpenChange}
                    modal={!tourActive && !accountMenuLocked}
                    holdOpen={accountMenuLocked}
                    onAppearance={() => setAppearanceOpen(true)}
                    onSignOut={onSignOut}
                  />
                </>
              ) : isPending ? (
                <div className="h-8 w-28 animate-pulse bg-muted" />
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setAppearanceOpen(true)}
                    title="Appearance"
                  >
                    <SunMoon />
                    <span className="sr-only">Appearance</span>
                  </Button>
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/login">Sign in</Link>
                  </Button>
                  {registrationEnabled ? (
                    <Button asChild size="sm">
                      <Link to="/register">Sign up</Link>
                    </Button>
                  ) : null}
                </>
              )}
            </div>
          </div>
          {user ? <ProductNav pathname={pathname} onRepositoryRoute={Boolean(repoRoute)} /> : null}
          {!user ? <div className="border-b" /> : null}
        </header>

        <AppearanceDialog open={appearanceOpen} onOpenChange={setAppearanceOpen} />

        <main className="flex-1">{children}</main>
        {user ? (
          <Suspense fallback={null}>
            <OnboardingGuide user={user} />
          </Suspense>
        ) : null}
      </div>
    </OnboardingChromeProvider>
  )
}

const PRODUCT_NAV_ITEMS = [
  { href: '/', label: 'Home', icon: Home },
  { href: '/repositories', label: 'Repositories', icon: BookMarked },
  { href: '/explore', label: 'Explore', icon: Compass },
  { href: '/issues', label: 'Issues', icon: CircleDot },
  { href: '/pulls', label: 'Pull requests', icon: GitPullRequest },
  { href: '/orgs', label: 'Organizations', icon: Building2 },
] as const

function ProductNav({ pathname, onRepositoryRoute }: { pathname: string; onRepositoryRoute: boolean }) {
  return (
    <nav aria-label="Primary" className="border-y" data-tour="product-nav">
      <div className="mx-auto flex max-w-7xl items-stretch gap-1 overflow-x-auto overflow-y-hidden px-3 sm:px-4 lg:px-8 py-4">
        {PRODUCT_NAV_ITEMS.map((item) => {
          const active =
            item.href === '/'
              ? pathname === '/'
              : item.href === '/repositories'
                ? pathname === item.href || onRepositoryRoute
                : pathname === item.href || pathname.startsWith(`${item.href}/`)
          const Icon = item.icon
          return (
            <Link
              key={item.href}
              to={item.href}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative flex shrink-0 items-center gap-1.5 px-2.5 text-xs text-muted-foreground outline-none hover:bg-muted/45 hover:text-foreground focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
                active &&
                'text-foreground after:absolute after:inset-x-2.5 after:-bottom-1 after:h-0.5 after:bg-primary',
              )}
            >
              <Icon className={cn('size-3.5', active && 'text-primary')} aria-hidden="true" />
              {item.label}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}

function preventMenuDismiss(event: { preventDefault: () => void }) {
  event.preventDefault()
}

function UserMenu({
  compact = false,
  displayName,
  email,
  image,
  initials,
  username,
  open,
  onOpenChange,
  modal = true,
  holdOpen = false,
  onAppearance,
  onSignOut,
}: {
  compact?: boolean
  displayName: string
  email: string | null
  image: string | null
  initials: string
  username: string | null
  open?: boolean
  onOpenChange?: (open: boolean) => void
  modal?: boolean
  holdOpen?: boolean
  onAppearance: () => void
  onSignOut: () => void
}) {
  const { appearance } = useAppearance()

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={modal}>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`Open account menu for ${displayName}`}
          data-tour="account-menu"
          className={cn(
            'flex items-center gap-2 border px-2 py-1 text-left hover:bg-muted',
            compact && 'px-1.5',
          )}
        >
          <Avatar className="size-7">
            {image ? <AvatarImage src={image} alt={displayName} /> : null}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-32 truncate text-xs sm:inline">{displayName}</span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn('w-60', holdOpen && 'z-[10001]')}
        onPointerDownOutside={holdOpen ? preventMenuDismiss : undefined}
        onFocusOutside={holdOpen ? preventMenuDismiss : undefined}
        onInteractOutside={holdOpen ? preventMenuDismiss : undefined}
        onEscapeKeyDown={holdOpen ? preventMenuDismiss : undefined}
      >
        <DropdownMenuLabel>
          <p className="font-medium">{displayName}</p>
          {email ? <p className="text-xs font-normal text-muted-foreground">{email}</p> : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {username ? (
            <DropdownMenuItem asChild>
              <Link to={`/${username}`} data-tour="your-profile">
                <UserRound />
                Your profile
              </Link>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem asChild>
            <Link to="/">
              <Home />
              Home
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/repositories">
              <BookMarked />
              Your repositories
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/orgs">
              <Building2 />
              Your organizations
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings">
              <Settings />
              Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/keys">
              <KeyRound />
              Keys & tokens
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/report">
              <Flag />
              Report abuse
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            onSelect={() => {
              setTimeout(onAppearance, 0)
            }}
          >
            <SunMoon />
            Appearance
            <DropdownMenuShortcut className="tracking-normal">
              {APPEARANCE_LABELS[appearance]}
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={onSignOut} variant="destructive">
            <LogOut />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
